import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { setupApi } from '../api'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { Toggle } from '../components/ui/Toggle'
import { ApiError } from '../api/client'

type Step = 0 | 1 | 2 | 3 | 4 | 5

interface FormState {
  admin: { email: string; password: string; confirm: string; displayName: string }
  general: { appName: string; defaultTheme: 'dark' | 'light'; localAuthEnabled: boolean }
  oidc: {
    enabled: boolean
    providerName: string
    issuer: string
    clientId: string
    clientSecret: string
    redirectUri: string
    scopes: string
  }
  turn: { stunUrls: string; turnUrl: string; turnUsername: string; turnPassword: string }
}

const DEFAULT_STATE: FormState = {
  admin: { email: '', password: '', confirm: '', displayName: '' },
  general: { appName: 'family-vc', defaultTheme: 'dark', localAuthEnabled: true },
  oidc: {
    enabled: false,
    providerName: 'Pocket ID',
    issuer: '',
    clientId: '',
    clientSecret: '',
    redirectUri: '',
    scopes: 'openid email profile',
  },
  turn: { stunUrls: 'stun:stun.l.google.com:19302', turnUrl: '', turnUsername: '', turnPassword: '' },
}

const STEPS = [
  'Administrator',
  'Allgemein',
  'OIDC / SSO',
  'TURN / STUN',
  'Reverse Proxy',
  'Abschluss',
] as const

export function SetupWizardPage() {
  const [step, setStep] = useState<Step>(0)
  const [form, setForm] = useState<FormState>(DEFAULT_STATE)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const navigate = useNavigate()
  const auth = useAuth()
  const toast = useToast()

  function validate(current: Step): boolean {
    const e: Record<string, string> = {}
    if (current === 0) {
      if (!form.admin.email) e.email = 'E-Mail ist erforderlich'
      if (!form.admin.displayName) e.displayName = 'Name ist erforderlich'
      if (!form.admin.password || form.admin.password.length < 8)
        e.password = 'Mindestens 8 Zeichen'
      if (form.admin.password !== form.admin.confirm) e.confirm = 'Passwörter stimmen nicht überein'
    }
    if (current === 2 && form.oidc.enabled) {
      if (!form.oidc.issuer) e.issuer = 'Issuer URL erforderlich'
      if (!form.oidc.clientId) e.clientId = 'Client ID erforderlich'
      if (!form.oidc.clientSecret) e.clientSecret = 'Client Secret erforderlich'
      if (!form.oidc.redirectUri) e.redirectUri = 'Redirect URI erforderlich'
      else if (!form.oidc.redirectUri.endsWith('/callback'))
        e.redirectUri = 'Muss mit /callback enden'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function next() {
    if (!validate(step)) return
    setStep((s) => Math.min(STEPS.length - 1, s + 1) as Step)
  }
  function prev() {
    setStep((s) => Math.max(0, s - 1) as Step)
  }

  async function submit() {
    if (!validate(0) || !validate(2)) {
      toast.push('Bitte Eingaben prüfen', 'error')
      return
    }
    setSubmitting(true)
    try {
      await setupApi.complete({
        admin: {
          email: form.admin.email,
          password: form.admin.password,
          displayName: form.admin.displayName,
        },
        general: form.general,
        oidc: form.oidc.enabled
          ? { ...form.oidc }
          : { enabled: false },
        turn: form.turn,
      })
      toast.push('Einrichtung abgeschlossen', 'success')
      await auth.refresh()
      navigate('/login', { replace: true })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Einrichtung fehlgeschlagen'
      toast.push(msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="wizard-shell">
      <div className="wizard">
        <div className="wizard-header">
          <h1>Willkommen bei family-vc</h1>
          <p className="muted">In wenigen Schritten ist alles startklar.</p>
          <ol className="stepper">
            {STEPS.map((label, i) => (
              <li key={label} className={i === step ? 'active' : i < step ? 'done' : ''}>
                <span className="num">{i + 1}</span>
                <span className="label">{label}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="wizard-body">
          {step === 0 && (
            <div className="stack">
              <h2>Administrator-Konto erstellen</h2>
              <Input
                label="Anzeigename"
                value={form.admin.displayName}
                error={errors.displayName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, admin: { ...f.admin, displayName: e.target.value } }))
                }
              />
              <Input
                type="email"
                label="E-Mail"
                value={form.admin.email}
                error={errors.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, admin: { ...f.admin, email: e.target.value } }))
                }
              />
              <Input
                type="password"
                label="Passwort"
                hint="Mindestens 8 Zeichen"
                value={form.admin.password}
                error={errors.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, admin: { ...f.admin, password: e.target.value } }))
                }
              />
              <Input
                type="password"
                label="Passwort bestätigen"
                value={form.admin.confirm}
                error={errors.confirm}
                onChange={(e) =>
                  setForm((f) => ({ ...f, admin: { ...f.admin, confirm: e.target.value } }))
                }
              />
            </div>
          )}

          {step === 1 && (
            <div className="stack">
              <h2>Allgemein</h2>
              <Input
                label="App-Name"
                value={form.general.appName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, general: { ...f.general, appName: e.target.value } }))
                }
              />
              <Select
                label="Standard-Theme"
                value={form.general.defaultTheme}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    general: { ...f.general, defaultTheme: v === 'light' ? 'light' : 'dark' },
                  }))
                }
                options={[
                  { value: 'dark', label: 'Dunkel' },
                  { value: 'light', label: 'Hell' },
                ]}
              />
              <Toggle
                label="Lokale Anmeldung aktivieren"
                hint="Wenn deaktiviert: nur SSO"
                checked={form.general.localAuthEnabled}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, general: { ...f.general, localAuthEnabled: v } }))
                }
              />
            </div>
          )}

          {step === 2 && (
            <div className="stack">
              <h2>OIDC / SSO</h2>
              <p className="muted">
                Optional. Empfohlen mit{' '}
                <a href="https://pocket-id.org/docs/introduction" target="_blank" rel="noreferrer">
                  Pocket ID
                </a>
                .
              </p>
              <Toggle
                label="OIDC aktivieren"
                checked={form.oidc.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, oidc: { ...f.oidc, enabled: v } }))}
              />
              {form.oidc.enabled && (
                <>
                  <Input
                    label="Provider-Name"
                    value={form.oidc.providerName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, oidc: { ...f.oidc, providerName: e.target.value } }))
                    }
                  />
                  <Input
                    label="Issuer URL"
                    placeholder="https://pocket-id.example.com"
                    value={form.oidc.issuer}
                    error={errors.issuer}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, oidc: { ...f.oidc, issuer: e.target.value } }))
                    }
                  />
                  <Input
                    label="Client ID"
                    value={form.oidc.clientId}
                    error={errors.clientId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, oidc: { ...f.oidc, clientId: e.target.value } }))
                    }
                  />
                  <Input
                    type="password"
                    label="Client Secret"
                    value={form.oidc.clientSecret}
                    error={errors.clientSecret}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, oidc: { ...f.oidc, clientSecret: e.target.value } }))
                    }
                  />
                  <Input
                    label="Redirect URI"
                    hint="Muss mit /callback enden, z.B. https://app.example.com/api/auth/oidc/callback"
                    placeholder={`${window.location.origin}/api/auth/oidc/callback`}
                    value={form.oidc.redirectUri}
                    error={errors.redirectUri}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, oidc: { ...f.oidc, redirectUri: e.target.value } }))
                    }
                  />
                  <Input
                    label="Scopes"
                    value={form.oidc.scopes}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, oidc: { ...f.oidc, scopes: e.target.value } }))
                    }
                  />
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="stack">
              <h2>TURN / STUN</h2>
              <p className="muted">
                Für zuverlässige Verbindungen hinter NAT/Firewall wird ein eigener TURN-Server
                empfohlen.
              </p>
              <Input
                label="STUN URLs (kommagetrennt)"
                value={form.turn.stunUrls}
                onChange={(e) =>
                  setForm((f) => ({ ...f, turn: { ...f.turn, stunUrls: e.target.value } }))
                }
              />
              <Input
                label="TURN URL"
                placeholder="turn:turn.example.com:3478"
                value={form.turn.turnUrl}
                onChange={(e) =>
                  setForm((f) => ({ ...f, turn: { ...f.turn, turnUrl: e.target.value } }))
                }
              />
              <Input
                label="TURN Benutzername"
                value={form.turn.turnUsername}
                onChange={(e) =>
                  setForm((f) => ({ ...f, turn: { ...f.turn, turnUsername: e.target.value } }))
                }
              />
              <Input
                type="password"
                label="TURN Passwort"
                value={form.turn.turnPassword}
                onChange={(e) =>
                  setForm((f) => ({ ...f, turn: { ...f.turn, turnPassword: e.target.value } }))
                }
              />
            </div>
          )}

          {step === 4 && (
            <div className="stack">
              <h2>Reverse Proxy (Zoraxy)</h2>
              <p className="muted">
                Konfigurieren Sie in Zoraxy einen HTTP-Eintrag auf die Frontend-URL (Port 8080 per
                Standard). Wichtige Punkte:
              </p>
              <ul className="bullets">
                <li>HTTPS-Termination am Proxy aktivieren</li>
                <li>WebSocket-Upgrade erlauben</li>
                <li>
                  X-Forwarded-* Header weiterreichen (family-vc nutzt <code>trustProxy</code>)
                </li>
                <li>
                  Beispiel-Pfade:
                  <ul>
                    <li>
                      <code>/</code> → Frontend Container (<code>family-vc-frontend:80</code>)
                    </li>
                    <li>
                      <code>/api</code> → Backend (<code>family-vc-backend:3001</code>) — der
                      Frontend-Nginx leitet intern bereits weiter, daher reicht meist ein einziger
                      Proxy-Eintrag auf den Frontend-Container.
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          )}

          {step === 5 && (
            <div className="stack">
              <h2>Fertig</h2>
              <p>
                Klicken Sie auf <strong>Abschließen</strong>, um den Administrator anzulegen und die
                Einstellungen zu speichern.
              </p>
              <div className="summary">
                <div>
                  <strong>Admin:</strong> {form.admin.email}
                </div>
                <div>
                  <strong>OIDC:</strong> {form.oidc.enabled ? form.oidc.providerName : 'deaktiviert'}
                </div>
                <div>
                  <strong>TURN:</strong> {form.turn.turnUrl || 'nur STUN'}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="wizard-nav">
          <Button variant="ghost" onClick={prev} disabled={step === 0}>
            Zurück
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next}>Weiter</Button>
          ) : (
            <Button onClick={submit} loading={submitting}>
              Abschließen
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
